import Link from "next/link";
import PhoneInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";
import type { Batch } from "@/lib/types";
import { useTranslations } from "@/i18n/LocaleProvider";
import PhoneOtpAuth from "@/components/PhoneOtpAuth";
import EmailVerificationField from "@/components/EmailVerificationField";
import { REQUIRE_PHONE_VERIFICATION } from "@/lib/featureFlags";

export default function BatchDetailsStep({
  // Not currently rendered -- see featureFlags.ts's SHOW_PRICING. Kept in the prop contract
  // (not destructured) so re-adding a price mention here later doesn't need a call-site change.
  name,
  onNameChange,
  email,
  onEmailChange,
  emailVerified,
  onEmailVerified,
  phone,
  onPhoneChange,
  phoneVerified,
  onPhoneVerified,
  termsAccepted,
  onTermsAcceptedChange,
  marketingOptIn,
  onMarketingOptInChange,
  couponCode,
  onCouponCodeChange,
  loading,
  onBack,
  onSubmit,
}: {
  batch: Batch;
  name: string;
  onNameChange: (value: string) => void;
  email: string;
  onEmailChange: (value: string) => void;
  emailVerified: boolean;
  onEmailVerified: () => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  phoneVerified: boolean;
  onPhoneVerified: () => void;
  termsAccepted: boolean;
  onTermsAcceptedChange: (value: boolean) => void;
  marketingOptIn: boolean;
  onMarketingOptInChange: (value: boolean) => void;
  couponCode: string;
  onCouponCodeChange: (value: string) => void;
  loading: boolean;
  onBack?: () => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslations();
  // See featureFlags.ts: with verification off this just requires the plain fields be filled
  // in -- functions/src/callable/enrollInBatch.ts still requires a verified phone server-side
  // regardless, so submitting here will fail until REQUIRE_PHONE_VERIFICATION is back on.
  const canSubmit = REQUIRE_PHONE_VERIFICATION
    ? termsAccepted && phoneVerified && (email.trim() === "" || emailVerified)
    : termsAccepted && name.trim() !== "" && phone.trim() !== "";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="mt-3 space-y-2.5 sm:mt-4 sm:space-y-3"
    >
      <div className="relative flex items-center justify-center">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="absolute left-0 text-sm font-medium text-muted transition hover:text-foreground"
          >
            &larr; {t("book.back")}
          </button>
        )}
        <h1 className="font-display text-xl sm:text-2xl">{t("batches.details.title")}</h1>
      </div>
      <div className="surface-card space-y-2.5 p-4 sm:space-y-3 sm:p-5">
        <input
          required
          placeholder={t("batches.details.namePlaceholder")}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
        />

        {REQUIRE_PHONE_VERIFICATION ? (
          <PhoneOtpAuth
            phone={phone}
            onPhoneChange={onPhoneChange}
            onVerified={onPhoneVerified}
            bordered={false}
            initiallyVerified={phoneVerified}
          />
        ) : (
          <div className="space-y-1.5">
            <label htmlFor="batch-phone-input" className="text-[0.8rem] font-medium tracking-wide text-foreground/70">
              {t("batches.details.phonePlaceholder")}
            </label>
            <PhoneInput
              id="batch-phone-input"
              international
              defaultCountry="IN"
              countryCallingCodeEditable={false}
              value={phone}
              onChange={(value) => onPhoneChange(value ?? "")}
              className="phone-input"
              numberInputProps={{ required: true }}
              flags={flags}
            />
          </div>
        )}

        {REQUIRE_PHONE_VERIFICATION ? (
          phoneVerified && (
            <EmailVerificationField
              email={email}
              onEmailChange={onEmailChange}
              onVerified={onEmailVerified}
              initiallyVerified={emailVerified}
            />
          )
        ) : (
          <input
            type="email"
            placeholder={t("batches.details.emailPlaceholder")}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
          />
        )}

        <input
          placeholder={t("batches.details.couponPlaceholder")}
          value={couponCode}
          onChange={(e) => onCouponCodeChange(e.target.value)}
          className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
        />

        <div className="space-y-1 sm:space-y-1.5">
          <label className="flex items-start gap-3 text-sm">
            <input
              required
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => onTermsAcceptedChange(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              {t("batches.details.termsPrefix")}{" "}
              <Link href="/terms" target="_blank" className="text-accent underline">
                {t("batches.details.termsLink")}
              </Link>{" "}
              {t("batches.details.and")}{" "}
              <Link href="/privacy" target="_blank" className="text-accent underline">
                {t("batches.details.privacyLink")}
              </Link>
              .
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => onMarketingOptInChange(e.target.checked)}
              className="mt-0.5"
            />
            <span>{t("batches.details.marketingOptIn")}</span>
          </label>
        </div>

        <p className="text-sm text-muted">{t("batches.details.holdNotice")}</p>
      </div>
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={loading || !canSubmit}
          className="rounded-xl bg-accent px-8 py-3 font-semibold text-accent-foreground disabled:opacity-50"
        >
          {loading ? t("batches.details.submitLoading") : t("batches.details.submitIdle")}
        </button>
      </div>
    </form>
  );
}
